// Shared 24-hour time picker.
//
// Replaces the native `<TextField type="time">` that every time field in this
// app used to render. A native time input follows the BROWSER/OS locale, so on
// a machine configured for en-US it renders a 12-hour AM/PM field — wrong for
// Israeli realtors, and slow (three separate segments to tab through, plus an
// AM/PM toggle) for the one thing they do constantly: set a meeting to 14:30.
//
// Control choice — MUI `Autocomplete` in `freeSolo` mode over a generated list
// of "HH:mm" slots. It is the only single control that serves both fast paths
// with no mode switch and no secondary "other time…" dialog:
//
//   * PICK — focusing opens the list already scrolled to the current selection
//     (or to `anchorTime` when nothing is set), so any quarter hour is one
//     click/tap away.
//   * TYPE — "14", "1430", "14:30" and "14.30" all resolve to 14:30, so an
//     off-grid time (14:37) costs a few keystrokes rather than a mode switch.
//
// A clock dial (`@material-ui/lab`'s MobileTimePicker) was rejected: it costs
// two drags plus a confirm for a single time, it cannot be typed into, and in
// this repo it would render as a 12-HOUR dial regardless — the lab version
// installed here decides that from the date-fns locale, and `src/index.tsx`
// historically fed `LocalizationProvider` a prop name it does not read.
//
// The public value contract is a plain "HH:mm" string (or "" when unset), which
// is exactly what the native inputs produced — call sites keep their existing
// parsing untouched.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@material-ui/core";
import type { FocusEvent } from "react";
import type { SxProps } from "@material-ui/system";
import type { Theme } from "@material-ui/core/styles";

// ----------------------------------------------------------------------

const MINUTES_PER_DAY = 24 * 60;

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/**
 * Every "HH:mm" slot of the day at `stepMinutes` granularity.
 * 15 minutes => 96 options, which is a comfortable scroll and covers the
 * overwhelming majority of real meeting times.
 */
export function buildTimeSlots(stepMinutes: number): string[] {
  const step = Math.max(1, Math.floor(stepMinutes));
  const slots: string[] = [];

  for (let minutes = 0; minutes < MINUTES_PER_DAY; minutes += step) {
    slots.push(`${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`);
  }

  return slots;
}

/**
 * Free text -> canonical "HH:mm", or `null` when the text is not a time.
 *
 * Accepted: "14:30" | "14.30" | "14-30" | "1430" | "930" | "14" | "9".
 * Rejected: "25:00", "14:70", "abc", "" — all yield `null`, and every caller
 * treats `null` as "keep whatever was there before" so a typo can never wipe a
 * previously chosen time.
 */
export function parseTimeInput(raw: string): string | null {
  const text = (raw ?? "").trim();
  if (!text) return null;

  let hours: number;
  let minutes: number;

  // The `-` is last in the class, so it is a literal, not a range.
  const separated = /^(\d{1,2})\s*[:.-]\s*(\d{1,2})$/.exec(text);

  if (separated) {
    hours = Number(separated[1]);
    minutes = Number(separated[2]);
  } else {
    const digitsOnly = /^(\d{1,4})$/.exec(text);
    if (!digitsOnly) return null;

    const digits = digitsOnly[1];

    if (digits.length <= 2) {
      // "9" -> 09:00, "14" -> 14:00
      hours = Number(digits);
      minutes = 0;
    } else if (digits.length === 3) {
      // "930" -> 09:30
      hours = Number(digits.slice(0, 1));
      minutes = Number(digits.slice(1));
    } else {
      // "1430" -> 14:30
      hours = Number(digits.slice(0, 2));
      minutes = Number(digits.slice(2));
    }
  }

  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23) return null;
  if (minutes < 0 || minutes > 59) return null;

  return `${pad2(hours)}:${pad2(minutes)}`;
}

/**
 * Which slots to show for the text currently in the field.
 *
 * Browse mode (whole day) when nothing is typed, or while the field still shows
 * the committed value — otherwise merely focusing a field reading "14:30" would
 * narrow the list to a single row and there would be nothing to browse.
 *
 * Otherwise prefix-match on digits, so "14" -> 14:00/14:15/14:30/14:45.
 *
 * An empty result for a no-match ("1437") is deliberate, not a fallback gap:
 * Autocomplete renders no popup at all for an empty option list when `freeSolo`
 * is set, so the off-grid value the user typed commits cleanly on Enter/blur
 * instead of being shadowed by a stale list.
 */
export function filterTimeOptions(
  options: string[],
  typed: string,
  committed: string
): string[] {
  const text = (typed ?? "").trim();
  if (!text || text === committed) return options;

  const digits = text.replace(/\D/g, "");
  if (!digits) return options;

  // A lone "9" should reach 09:xx (no hour actually starts with a bare 9).
  const prefixes = digits.length === 1 ? [digits, `0${digits}`] : [digits];

  return options.filter((option) => {
    const flat = option.replace(":", "");
    return prefixes.some((prefix) => flat.indexOf(prefix) === 0);
  });
}

// React 17 has no useId; a module counter gives each instance the stable id
// Autocomplete needs to derive its listbox id (`${id}-listbox`), which is how
// the open-scroll below finds the list.
let instanceCounter = 0;

// ----------------------------------------------------------------------

export type TimeSelectProps = {
  /** Field label. Passed in so each call site keeps its own i18n key. */
  label: string;
  /** Canonical "HH:mm", or "" when no time is set. */
  value: string;
  /** Fired only with a valid "HH:mm" — or "" when cleared and `clearable`. */
  onChange: (value: string) => void;
  disabled?: boolean;
  /**
   * Allow emptying the field back to "". Off by default: most call sites
   * (a meeting's start/end) must always hold a time. The calendar's TASK time
   * turns it on, because "" there means "all-day task, no specific hour".
   */
  clearable?: boolean;
  /** Slot granularity in minutes. Off-grid times stay typeable regardless. */
  step?: number;
  /** Where the list scrolls when `value` is empty. */
  anchorTime?: string;
  variant?: "standard" | "outlined" | "filled";
  size?: "small" | "medium";
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
};

export function TimeSelect({
  label,
  value,
  onChange,
  disabled = false,
  clearable = false,
  step = 15,
  anchorTime = "09:00",
  variant = "outlined",
  size,
  fullWidth = false,
  sx
}: TimeSelectProps): JSX.Element {
  const slots = useMemo(() => buildTimeSlots(step), [step]);

  const [instanceId] = useState(() => `time-select-${(instanceCounter += 1)}`);
  const [inputValue, setInputValue] = useState<string>(value ?? "");

  // Mirror an externally-changed value into the text field. Deps are on `value`
  // alone, so this cannot clobber text the user is in the middle of typing —
  // it only fires when the committed value actually changed.
  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  /**
   * The single write path. Anything unparseable restores the last good value
   * instead of propagating, so the field can never be left holding garbage and
   * `onChange` never receives a non-"HH:mm" string.
   */
  const commit = useCallback(
    (text: string): void => {
      const trimmed = (text ?? "").trim();

      if (!trimmed) {
        if (clearable) {
          setInputValue("");
          if (value !== "") onChange("");
        } else {
          setInputValue(value ?? "");
        }
        return;
      }

      const parsed = parseTimeInput(trimmed);

      if (parsed === null) {
        setInputValue(value ?? "");
        return;
      }

      setInputValue(parsed);
      if (parsed !== value) onChange(parsed);
    },
    [clearable, onChange, value]
  );

  /**
   * Centre the current selection (or `anchorTime`) in the open list.
   *
   * Autocomplete already scrolls to a value that matches an option, but it
   * scrolls it to the top edge and does nothing at all when the value is empty
   * — which would open a 96-row list at 00:00 and force a realtor to scroll
   * past the entire night to reach the afternoon. Runs in rAF so it lands after
   * the popup has been laid out, and uses the same offsetTop arithmetic
   * Autocomplete itself uses.
   */
  const scrollSelectionIntoView = useCallback((): void => {
    const target = value && slots.indexOf(value) !== -1 ? value : anchorTime;

    const run = (retry: boolean): void => {
      const listbox = document.getElementById(`${instanceId}-listbox`);

      if (!listbox) {
        if (retry) requestAnimationFrame(() => run(false));
        return;
      }

      const options = Array.from(
        listbox.querySelectorAll<HTMLElement>('[role="option"]')
      );
      const match = options.find((option) => (option.textContent ?? "").trim() === target);
      if (!match) return;

      listbox.scrollTop = Math.max(
        0,
        match.offsetTop - listbox.clientHeight / 2 + match.offsetHeight / 2
      );
    };

    requestAnimationFrame(() => run(true));
  }, [anchorTime, instanceId, slots, value]);

  return (
    <Autocomplete
      freeSolo
      openOnFocus
      // freeSolo defaults selectOnFocus to false; without it, focusing a field
      // holding "09:00" and typing "1" gives "09:001" instead of "1".
      selectOnFocus
      // freeSolo also hides the dropdown arrow by default. Keep it — it is the
      // only visual cue that this field offers a list at all.
      forcePopupIcon
      disabled={disabled}
      disableClearable={!clearable}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      id={instanceId}
      options={slots}
      value={value ?? ""}
      inputValue={inputValue}
      onOpen={scrollSelectionIntoView}
      onInputChange={(_event, next, reason) => {
        // Only user keystrokes drive the text; every other transition is owned
        // by `commit` / the value-sync effect above.
        if (reason === "input") setInputValue(next);
      }}
      onChange={(_event, next) => {
        // `next` is null when the clear button is pressed, the picked slot when
        // an option is chosen, and the raw typed text on Enter (freeSolo).
        commit(next === null ? "" : next);
      }}
      filterOptions={(options, state) =>
        filterTimeOptions(options, state.inputValue, value ?? "")
      }
      ListboxProps={{ style: { maxHeight: 288 } }}
      renderOption={(optionProps, option) => (
        // The time itself is LTR content inside an RTL menu.
        <li {...optionProps} key={option} dir="ltr" style={{ justifyContent: "flex-start" }}>
          {option}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          inputProps={{
            ...params.inputProps,
            // LTR so a partially typed "14:" keeps its colon on the right, and
            // a numeric keypad on mobile rather than the full Hebrew keyboard.
            dir: "ltr",
            inputMode: "numeric",
            placeholder: "--:--",
            "aria-label": label,
            onBlur: (event) => {
              // TextField types inputProps for the multiline case too, so the
              // handler arrives as input-or-textarea; Autocomplete's own
              // listener is input-only. It is always the input here.
              params.inputProps.onBlur?.(event as FocusEvent<HTMLInputElement>);
              commit(event.target.value);
            }
          }}
        />
      )}
    />
  );
}

export default TimeSelect;
